import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, ArrowForwardIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./navbar";
import Footer from "./footer";
import { SERVICES } from "./config";
import { theme } from "./theme";

const MotionBox = motion.create(Box);

export default function ServiceDetailPage({ service }) {
  const {
    pageBg,
    sectionBg,
    cardBg,
    textColor,
    textSecondary,
    accentColor,
    accentCyan,
  } = theme.colors;
  const relatedServices = SERVICES.filter((item) =>
    service.related?.includes(item.name)
  );

  return (
    <Box bg={pageBg} color={textColor} minH="100vh">
      <Navbar />

      <MotionBox
        id="main-content"
        as="main"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 10, md: 16 }}>
          <Button
            as={RouterLink}
            to="/services"
            variant="ghost"
            leftIcon={<ArrowBackIcon />}
            color={textSecondary}
            mb={8}
            px={0}
            _hover={{ color: accentCyan, bg: "transparent" }}
          >
            Back to Services
          </Button>

          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={{ base: 8, lg: 12 }}
            align="stretch"
          >
            <Stack spacing={6} flex="1.15">
              <HStack spacing={3} flexWrap="wrap">
                <Badge
                  bg={`${service.color}22`}
                  color={service.color}
                  border="1px solid"
                  borderColor={`${service.color}55`}
                  borderRadius="full"
                  px={4}
                  py={1}
                >
                  {service.eyebrow}
                </Badge>
                <Badge
                  bg="rgba(0, 255, 208, 0.1)"
                  color={accentCyan}
                  border="1px solid rgba(0, 255, 208, 0.24)"
                  borderRadius="full"
                  px={4}
                  py={1}
                >
                  {service.timeline}
                </Badge>
              </HStack>

              <Heading
                as="h1"
                fontSize={{ base: "3xl", md: "5xl" }}
                lineHeight="1.08"
                maxW="820px"
              >
                {service.title}
              </Heading>

              <Text fontSize={{ base: "md", md: "xl" }} color={textSecondary} lineHeight="1.8">
                {service.description}
              </Text>

              <HStack spacing={3} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to="/contact"
                  bg={accentColor}
                  color="white"
                  rightIcon={<ArrowForwardIcon />}
                  borderRadius="lg"
                  px={6}
                  _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}
                  _active={{ transform: "translateY(0)" }}
                >
                  {service.cta}
                </Button>
                <Button
                  as={RouterLink}
                  to="/portfolio"
                  variant="outline"
                  borderColor="rgba(0, 255, 208, 0.45)"
                  color={accentCyan}
                  borderRadius="lg"
                  px={6}
                  _hover={{ bg: "rgba(0, 255, 208, 0.08)" }}
                >
                  See Portfolio
                </Button>
              </HStack>
            </Stack>

            <Box
              flex="0.85"
              bg="rgba(15, 23, 42, 0.78)"
              border="1px solid rgba(255, 255, 255, 0.12)"
              borderRadius="xl"
              p={{ base: 6, md: 7 }}
              boxShadow="0 18px 44px rgba(0, 0, 0, 0.22)"
            >
              <Flex align="center" gap={4} mb={6}>
                <Flex
                  align="center"
                  justify="center"
                  w={14}
                  h={14}
                  borderRadius="lg"
                  bg={`${service.color}1A`}
                  border="1px solid"
                  borderColor={`${service.color}55`}
                >
                  <Icon as={service.icon} w={7} h={7} color={service.color} />
                </Flex>
                <Box>
                  <Text color={textSecondary} fontSize="sm">
                    Good fit for
                  </Text>
                  <Heading size="sm">{service.bestFor}</Heading>
                </Box>
              </Flex>

              <VStack align="stretch" spacing={4}>
                {service.snapshot.map((item) => (
                  <Box
                    key={item.label}
                    borderTop="1px solid rgba(255, 255, 255, 0.1)"
                    pt={4}
                  >
                    <Text color={textSecondary} fontSize="sm" mb={1}>
                      {item.label}
                    </Text>
                    <Text fontWeight="semibold">{item.value}</Text>
                  </Box>
                ))}
              </VStack>
            </Box>
          </Stack>
        </Container>

        <Box bg={sectionBg} py={{ base: 12, md: 16 }}>
          <Container maxW="1360px" px={{ base: 4, md: 8 }}>
            <SectionHeading
              eyebrow="What You Get"
              title="Clear capabilities without the guesswork"
              text={service.featuresIntro}
            />
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
              {service.features.map((feature) => (
                <FeatureCard key={feature.title} feature={feature} color={service.color} />
              ))}
            </SimpleGrid>
          </Container>
        </Box>

        {service.architecture && (
          <ArchitectureDeepDive
            architecture={service.architecture}
            color={service.color}
            accentCyan={accentCyan}
            cardBg={cardBg}
            textColor={textColor}
            textSecondary={textSecondary}
          />
        )}

        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 12, md: 16 }}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={{ base: 12, md: 16 }}>
            <InfoPanel
              title="Outcomes"
              items={service.outcomes}
              color={service.color}
              textSecondary={textSecondary}
            />
            <InfoPanel
              title="Included"
              items={service.included}
              color={accentCyan}
              textSecondary={textSecondary}
            />
          </SimpleGrid>

          <SectionHeading
            eyebrow="Delivery"
            title="A simple process from first call to launch"
            text={service.processIntro}
          />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
            {service.process.map((step, index) => (
              <Box
                key={step.title}
                bg={cardBg}
                border="1px solid rgba(255, 255, 255, 0.12)"
                borderRadius="xl"
                p={6}
              >
                <Badge bg={`${service.color}22`} color={service.color} borderRadius="full" mb={4}>
                  Step {index + 1}
                </Badge>
                <Heading size="sm" mb={3}>
                  {step.title}
                </Heading>
                <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                  {step.text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>

        {relatedServices.length > 0 && (
          <Box bg="rgba(15, 23, 42, 0.72)" py={{ base: 12, md: 14 }}>
            <Container maxW="1360px" px={{ base: 4, md: 8 }}>
              <Flex
                align={{ base: "start", md: "center" }}
                justify="space-between"
                gap={5}
                direction={{ base: "column", md: "row" }}
                mb={6}
              >
                <Box>
                  <Text color={accentCyan} fontWeight="bold" mb={2}>
                    Related Services
                  </Text>
                  <Heading size="lg">Useful next pieces</Heading>
                </Box>
                <Button
                  as={RouterLink}
                  to="/services"
                  variant="outline"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  color={textColor}
                  _hover={{ bg: "rgba(255, 255, 255, 0.08)" }}
                >
                  Browse All
                </Button>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: relatedServices.length }} spacing={4}>
                {relatedServices.map((related) => (
                  <Flex
                    key={related.name}
                    as={RouterLink}
                    to={related.path}
                    align="center"
                    justify="space-between"
                    gap={4}
                    p={5}
                    bg={cardBg}
                    border="1px solid rgba(255, 255, 255, 0.12)"
                    borderRadius="xl"
                    color={textColor}
                    textDecoration="none"
                    _hover={{
                      borderColor: `${related.color}88`,
                      textDecoration: "none",
                      transform: "translateY(-2px)",
                    }}
                    transition="all 0.2s"
                  >
                    <HStack spacing={4}>
                      <Icon as={related.icon} color={related.color} boxSize={6} />
                      <Text fontWeight="bold">{related.name}</Text>
                    </HStack>
                    <ArrowForwardIcon color={related.color} />
                  </Flex>
                ))}
              </SimpleGrid>
            </Container>
          </Box>
        )}

        <Box py={{ base: 12, md: 16 }}>
          <Container maxW="850px" px={{ base: 4, md: 8 }} textAlign="center">
            <Heading size="xl" mb={4}>
              Ready to talk through the right version for you?
            </Heading>
            <Text color={textSecondary} fontSize="lg" lineHeight="1.8" mb={7}>
              Share where you are now, what you want to build, and the constraints
              you already know. We will help shape the next step.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={accentColor}
              color="white"
              rightIcon={<ArrowForwardIcon />}
              borderRadius="lg"
              px={7}
              _hover={{ bg: "#ff853d" }}
            >
              Contact Orvitrix
            </Button>
          </Container>
        </Box>
      </MotionBox>

      <Footer />
    </Box>
  );
}

function SectionHeading({ eyebrow, title, text }) {
  const { textSecondary, accentCyan } = theme.colors;

  return (
    <Box mb={8}>
      <Text color={accentCyan} fontWeight="bold" mb={2}>
        {eyebrow}
      </Text>
      <Heading size={{ base: "lg", md: "xl" }} mb={3}>
        {title}
      </Heading>
      <Text color={textSecondary} maxW="760px" lineHeight="1.8">
        {text}
      </Text>
    </Box>
  );
}

function FeatureCard({ feature, color }) {
  const { cardBg, textColor, textSecondary } = theme.colors;

  return (
    <Box
      bg={cardBg}
      border="1px solid rgba(255, 255, 255, 0.12)"
      borderRadius="xl"
      p={6}
      minH="100%"
      transition="all 0.2s"
      _hover={{ transform: "translateY(-4px)", borderColor: `${color}88` }}
    >
      <Flex
        align="center"
        justify="center"
        w={12}
        h={12}
        borderRadius="lg"
        bg={`${color}1A`}
        border="1px solid"
        borderColor={`${color}55`}
        mb={5}
      >
        <Icon as={feature.icon} color={color} boxSize={6} />
      </Flex>
      <Heading size="sm" color={textColor} mb={3}>
        {feature.title}
      </Heading>
      <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
        {feature.text}
      </Text>
    </Box>
  );
}

function ArchitectureDeepDive({
  architecture,
  color,
  accentCyan,
  cardBg,
  textColor,
  textSecondary,
}) {
  return (
    <Box bg="rgba(5, 10, 22, 0.7)" py={{ base: 12, md: 16 }}>
      <Container maxW="1360px" px={{ base: 4, md: 8 }}>
        <Stack direction={{ base: "column", xl: "row" }} spacing={8} align="stretch">
          <Box flex="0.95">
            <SectionHeading
              eyebrow={architecture.eyebrow}
              title={architecture.title}
              text={architecture.text}
            />

            <Box
              border="1px solid rgba(0, 255, 208, 0.2)"
              borderRadius="xl"
              bg="rgba(8, 13, 24, 0.72)"
              p={{ base: 5, md: 6 }}
              boxShadow="0 22px 58px rgba(0, 0, 0, 0.25)"
            >
              <Text color={accentCyan} fontWeight="bold" mb={4}>
                AI workflow path
              </Text>
              <Flex align="center" gap={2} flexWrap="wrap">
                {architecture.flow.map((step, index) => (
                  <HStack key={step} spacing={2}>
                    <Badge
                      bg={index === 3 ? "rgba(0, 255, 208, 0.14)" : "rgba(255, 255, 255, 0.08)"}
                      color={index === 3 ? accentCyan : textColor}
                      border="1px solid rgba(255, 255, 255, 0.12)"
                      borderRadius="md"
                      px={3}
                      py={1}
                    >
                      {step}
                    </Badge>
                    {index < architecture.flow.length - 1 && (
                      <ArrowForwardIcon color={textSecondary} boxSize={3} />
                    )}
                  </HStack>
                ))}
              </Flex>
            </Box>
          </Box>

          <Box flex="1.2">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {architecture.layers.map((layer) => (
                <Box
                  key={layer.title}
                  bg={cardBg}
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  borderRadius="xl"
                  p={5}
                >
                  <Flex align="center" gap={3} mb={3}>
                    <Flex
                      align="center"
                      justify="center"
                      w={10}
                      h={10}
                      borderRadius="lg"
                      bg={`${color}1A`}
                      border="1px solid"
                      borderColor={`${color}55`}
                      flexShrink={0}
                    >
                      <Icon as={layer.icon} color={color} boxSize={5} />
                    </Flex>
                    <Heading size="sm">{layer.title}</Heading>
                  </Flex>
                  <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                    {layer.text}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </Stack>

        <Box mt={8}>
          <Text color={accentCyan} fontWeight="bold" mb={4}>
            Example use cases
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {architecture.useCases.map((item) => (
              <HStack
                key={item}
                align="start"
                spacing={3}
                bg="rgba(15, 23, 42, 0.6)"
                border="1px solid rgba(255, 255, 255, 0.1)"
                borderRadius="lg"
                p={4}
              >
                <CheckCircleIcon color={accentCyan} mt="3px" />
                <Text color={textSecondary} fontSize="sm" lineHeight="1.7">
                  {item}
                </Text>
              </HStack>
            ))}
          </SimpleGrid>
        </Box>
      </Container>
    </Box>
  );
}

function InfoPanel({ title, items, color, textSecondary }) {
  const { cardBg } = theme.colors;

  return (
    <Box bg={cardBg} border="1px solid rgba(255, 255, 255, 0.12)" borderRadius="xl" p={7}>
      <Heading size="md" mb={5}>
        {title}
      </Heading>
      <VStack align="stretch" spacing={3}>
        {items.map((item) => (
          <HStack key={item} align="start" spacing={3}>
            <CheckCircleIcon color={color} mt="3px" />
            <Text color={textSecondary}>{item}</Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}
